import classNames from "classnames";
import React, { FunctionComponent, useRef } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Panel } from "../../../../states/customization";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { lowQualityPicturesByPage } from "../../../../utils/customization/box";
import { getCoverDefinitions, getCoverFilteredPages } from "../../../../utils/customization/cover";
import { ReactComponent as IconText } from "../../../../icons/16x16/type.svg";
import Button from "../../../atoms/Button";
import LowQualityIndicator from "../../../atoms/customization/LowQualityIndicator";
import Page from "../Page";
import PageBar from "../PageBar";
import "./style.scss";

interface Props {
  onLowQualityWarning?: (PicturesCount: number) => void;
  ppmm: number;
}

const CoverContent: FunctionComponent<Props> = ({ onLowQualityWarning, ppmm }) => {
  const {
    content,
    dispatch,
    selection,
    template,
    ui: { panel },
  } = useCustomizationContext();
  const intl = useIntl();

  const coverElement = useRef<HTMLDivElement>(null);
  const coverDefinitions = getCoverDefinitions(template.pageDefinitions);

  const frontCoverTemplateDefinitions: TemplatePageDefinition[] = [];
  const spineTemplateDefinitions: TemplatePageDefinition[] = [];
  coverDefinitions.forEach((pageDefinition) => {
    if (pageDefinition.type === "front-cover") {
      frontCoverTemplateDefinitions.push(pageDefinition);
    }
    if (pageDefinition.type === "spine") {
      spineTemplateDefinitions.push(pageDefinition);
    }
  });

  // get front and back cover pages
  const frontCoverPages = getCoverFilteredPages(content.pages, frontCoverTemplateDefinitions);
  const spinePages = getCoverFilteredPages(content.pages, spineTemplateDefinitions);

  const frontCover = frontCoverPages[0];
  const frontCoverDefinition = frontCoverTemplateDefinitions.find(
    ({ key }) => key === frontCover.pageDefinitionKey
  );
  const lowQualityPicturesCount = frontCover
    ? lowQualityPicturesByPage(
        frontCover.pageDefinitionKey,
        frontCover.editablePictures,
        selection,
        template
      )
    : 0;
  if (onLowQualityWarning && lowQualityPicturesCount > 0) {
    onLowQualityWarning(lowQualityPicturesCount);
  } else if (onLowQualityWarning && lowQualityPicturesCount === 0) {
    onLowQualityWarning(0);
  }

  const handleClickOnEditTitle = () => {
    dispatch({ type: "setPanel", panel: Panel.EDITTITLE });
  };

  const handleClickOnCoverHead = () => {
    dispatch({
      type: "setPanel",
      key: frontCover.key,
      openedTab: panel.openedTab,
      panel: Panel.EDITCOVER,
    });
  };

  const title =
    content.templateType === "book" && content.bookTitle
      ? content.bookTitle
      : intl.formatMessage({ id: "kustom_EditableBookTitle_placeholder" });

  const coverSelected = panel.current === Panel.EDITCOVER && panel.key === frontCover.key;

  const hasOptionsMenu =
    frontCoverTemplateDefinitions.length > 1 ||
    (!!frontCoverDefinition && frontCoverDefinition.backgroundColorKeys.length > 1);

  return (
    <div className="CoverContent">
      <div className="CoverContent-bookTitle">
        <span className="CoverContent-bookTitle-content">{title}</span>
        <Button type="primary-lightest" onClick={handleClickOnEditTitle}>
          <IconText />
          {intl.formatMessage({ id: "kustom_CoverContent_edit" })}
        </Button>
      </div>
      <div
        className={classNames("CoverContent-cover", {
          "CoverContent-cover--active": coverSelected,
        })}
      >
        <div className="CoverContent-cover-title" onClick={handleClickOnCoverHead}>
          <span className="CoverContent-cover-title-text">
            {intl.formatMessage({
              id: "kustom_CoverPanel_bookTitle_places_cover",
            })}
          </span>
          {lowQualityPicturesCount > 0 && (
            <LowQualityIndicator
              label={intl.formatMessage({
                id: "kustom_PageList_LowQuality_some",
              })}
            />
          )}
        </div>
        <div className="CoverContent-cover-pages" ref={coverElement}>
          <div className="CoverContent-spine" onClick={handleClickOnEditTitle}>
            {spinePages[0] && (
              <Page content={content} pageKey={spinePages[0].key} ppmm={ppmm} template={template} />
            )}
          </div>
          {frontCover && (
            <>
              <Page content={content} pageKey={frontCover.key} ppmm={ppmm} template={template} />
              <PageBar
                content={content}
                displayPanelCondition={hasOptionsMenu}
                isRotationPair={false}
                pageKey={frontCover.key}
                elementToShow={coverElement}
                type="cover"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverContent;
